import { OneCommunityResponseCampaign } from '@/types/common'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useAdminClient } from '@/lib/api'
import Loading from '@/components/loading'
import { Button } from '@/components/button'
import { useState } from 'react'

interface ValidationResult {
  campaignId: string
  campaignName: string | null
  error?: string
  inSync: boolean
  totalInOneCommunity: number
  totalInDatabase: number
  missingInOneCommunity: string[]
  missingInDatabase: string[]
}

export default function AdminPage() {
  const adminClient = useAdminClient()
  const [syncingIds, setSyncingIds] = useState<string[]>([])
  const [validatingIds, setValidatingIds] = useState<string[]>([])
  const [syncingMissingIds, setSyncingMissingIds] = useState<string[]>([])
  const [validationResults, setValidationResults] = useState<
    Record<string, ValidationResult>
  >({})

  const { data: campaignsResponse, isLoading: loading } = useQuery({
    queryKey: ['oneCommunityCampaigns'],
    queryFn: async () => await adminClient.getOneCommunityCampaigns.query(),
  })

  const syncCampaignMutation = useMutation({
    mutationFn: (campaignId: string) =>
      adminClient.syncOnecommunityCampaign.mutate({
        params: { campaignId },
        body: {},
      }),
  })

  const validateSyncMutation = useMutation({
    mutationFn: (campaignId: string) =>
      adminClient.validateOneCommunitySync.query({ params: { campaignId } }),
  })

  const syncMissingMutation = useMutation({
    mutationFn: ({
      campaignId,
      endUserIds,
    }: {
      campaignId: string
      endUserIds: string[]
    }) =>
      adminClient.syncMissingOneCommunityEntries.mutate({
        params: { campaignId },
        body: { endUserIds },
      }),
  })

  if (loading) return <Loading />

  const handleSync = async (campaignId: string) => {
    try {
      setSyncingIds([...syncingIds, campaignId])
      await syncCampaignMutation.mutateAsync(campaignId)
      alert('Sync initiated successfully!')
    } catch (error) {
      console.error('Error syncing campaign:', error)
      alert('Error syncing campaign')
    } finally {
      setSyncingIds(syncingIds.filter((id) => id !== campaignId))
    }
  }

  const handleValidate = async (campaignId: string) => {
    try {
      setValidatingIds([...validatingIds, campaignId])
      const response = await validateSyncMutation.mutateAsync(campaignId)
      if (response.status === 200) {
        setValidationResults((prev) => ({
          ...prev,
          [campaignId]: response.body,
        }))
      } else {
        alert('Error validating sync')
      }
    } catch (error) {
      console.error('Error validating sync:', error)
      alert('Error validating sync')
    } finally {
      setValidatingIds(validatingIds.filter((id) => id !== campaignId))
    }
  }

  const handleSyncMissing = async (
    campaignId: string,
    endUserIds: string[],
  ) => {
    try {
      setSyncingMissingIds([...syncingMissingIds, campaignId])
      const response = await syncMissingMutation.mutateAsync({
        campaignId,
        endUserIds,
      })
      if (response.status === 200) {
        alert(`Scheduled sync for ${response.body.scheduled} entries`)
        // Clear the validation result to prompt re-validation
        setValidationResults((prev) => {
          const newResults = { ...prev }
          delete newResults[campaignId]
          return newResults
        })
      } else {
        alert('Error scheduling sync')
      }
    } catch (error) {
      console.error('Error scheduling sync:', error)
      alert('Error scheduling sync')
    } finally {
      setSyncingMissingIds(syncingMissingIds.filter((id) => id !== campaignId))
    }
  }

  return (
    <div className="bg-ui-100 p-4 gap-4 space-y-2 flex flex-col h-full">
      <div className="bg-white px-8 rounded-md">
        <div className="grid grid-cols-6 text-lg font-bold py-8">
          <div>Campaign name</div>
          <div>Category</div>
          <div>Form IDs Entries</div>
          <div>Alphabait Form Entries</div>
          <div>Sync Status</div>
          <div>Actions</div>
        </div>
        {(
          campaignsResponse?.body as OneCommunityResponseCampaign[] | undefined
        )?.map((campaign: OneCommunityResponseCampaign) => {
          const validation = validationResults[campaign.id]
          return (
            <div className="grid grid-cols-6 my-4" key={campaign.id}>
              <div>{campaign.name}</div>
              <div>{campaign.campaignCategory}</div>
              <div>
                {campaign?.formIdsEntryCounts || 0}
                {campaign.optionalSettings?.oneCommunity?.formIds && (
                  <span className="text-xs text-gray-500 ml-1">
                    (From ID: {campaign.optionalSettings.oneCommunity.formIds})
                  </span>
                )}
              </div>
              <div>
                {campaign.alphabaitFormIdEntryCount || 0}
                {campaign.optionalSettings?.oneCommunity?.alphabaitFormId && (
                  <span className="text-xs text-gray-500 ml-1">
                    (From ID:{' '}
                    {campaign.optionalSettings.oneCommunity.alphabaitFormId})
                  </span>
                )}
              </div>
              <div>
                {validation ? (
                  <div className="text-sm">
                    {validation.error ? (
                      <span className="text-yellow-600">
                        {validation.error}
                      </span>
                    ) : validation.inSync ? (
                      <span className="text-green-600">In Sync</span>
                    ) : (
                      <div className="text-red-600">
                        <div>Out of Sync</div>
                        <div className="text-xs">
                          OC: {validation.totalInOneCommunity} | DB:{' '}
                          {validation.totalInDatabase}
                        </div>
                        {validation.missingInOneCommunity.length > 0 && (
                          <div className="text-xs">
                            Missing in OC:{' '}
                            {validation.missingInOneCommunity.length}
                          </div>
                        )}
                        {validation.missingInDatabase.length > 0 && (
                          <div className="text-xs">
                            Missing in DB: {validation.missingInDatabase.length}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-gray-400 text-sm">Not checked</span>
                )}
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  primary
                  label={
                    syncingIds.includes(campaign.id) ? 'Syncing...' : 'Sync'
                  }
                  size="small-icon"
                  disabled={syncingIds.includes(campaign.id)}
                  className="border-0 border-transparent hover:border-transparent focus:outline-none focus:ring-transparent w-20"
                  onClick={() => handleSync(campaign.id)}
                />
                <Button
                  label={
                    validatingIds.includes(campaign.id)
                      ? 'Checking...'
                      : 'Validate'
                  }
                  size="small-icon"
                  disabled={validatingIds.includes(campaign.id)}
                  className="border-0 border-transparent hover:border-transparent focus:outline-none focus:ring-transparent w-20"
                  onClick={() => handleValidate(campaign.id)}
                />
                {validation &&
                  !validation.error &&
                  validation.missingInOneCommunity.length > 0 && (
                    <Button
                      label={
                        syncingMissingIds.includes(campaign.id)
                          ? 'Scheduling...'
                          : `Sync Missing (${validation.missingInOneCommunity.length})`
                      }
                      size="small-icon"
                      disabled={syncingMissingIds.includes(campaign.id)}
                      className="border-0 border-transparent hover:border-transparent focus:outline-none focus:ring-transparent text-red-600"
                      onClick={() =>
                        handleSyncMissing(
                          campaign.id,
                          validation.missingInOneCommunity,
                        )
                      }
                    />
                  )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
